import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getApplication,
  getClosedDealAttachmentsByDealId,
  getClosedDealKycStatus,
  getDocumentByDealId
} from '../../services/api/implementation/impl';
import { evaluateKycCompleteness, evaluateKycVerification } from './postSalesKyc';
import DealApplicantsSection from './DealApplicantsSection';
import DealAgreementDocsSection from './DealAgreementDocsSection';
import DealKycDocumentsSection from './DealKycDocumentsSection';
import DealDocumentAttachments from '../../pages/DealDocumentAttachments';

const PostSalesDealHub = ({ deal, lead, onDealUpdate, readOnly = false }) => {
  const dealId = deal?.id;
  const projectId = deal?.unit?.project_id ?? lead?.deal?.unit?.project_id;

  const { data: applicationData, refetch: refetchApplicants } = useQuery({
    queryKey: ['application', dealId],
    queryFn: () => getApplication(),
    enabled: !!dealId,
    select: (rows) =>
      (rows || []).filter((r) => Number(r.deal_id) === Number(dealId)).sort(
        (a, b) => (a.applicant_number || 0) - (b.applicant_number || 0)
      )
  });

  const { data: attachmentResult, refetch: refetchAttachments } = useQuery({
    queryKey: ['closedDealAttachments', dealId],
    queryFn: () => getClosedDealAttachmentsByDealId(dealId),
    enabled: !!dealId
  });

  const { data: generatedDocs, refetch: refetchDocs } = useQuery({
    queryKey: ['dealGeneratedDocuments', dealId],
    queryFn: () => getDocumentByDealId(dealId),
    enabled: !!dealId && !readOnly
  });

  const { data: kycStatusRaw, refetch: refetchKycStatus } = useQuery({
    queryKey: ['closedDealKycStatus', dealId, 'hub'],
    queryFn: () => getClosedDealKycStatus(dealId),
    enabled: !!dealId
  });

  const attachments = attachmentResult?.attachments ?? [];
  const kyc = useMemo(() => evaluateKycCompleteness(attachments), [attachments]);
  const verification = useMemo(
    () => evaluateKycVerification(kycStatusRaw),
    [kycStatusRaw]
  );
  const applicantCount = applicationData?.length ?? 0;
  const agreementCount = Array.isArray(generatedDocs) ? generatedDocs.length : 0;

  const refreshAll = () => {
    refetchApplicants();
    refetchAttachments();
    refetchDocs();
    refetchKycStatus();
    onDealUpdate?.();
  };

  if (!dealId) {
    return (
      <div className="mt-2 flex min-h-[8rem] items-center justify-center rounded-xl border border-gray-200 bg-white text-sm text-gray-500">
        No closed deal found for this lead.
      </div>
    );
  }

  const kycComplete = verification.verified || kyc.complete;
  const kycTitle = verification.verified
    ? 'Verified'
    : kyc.complete
      ? verification.label || 'Complete'
      : 'Incomplete';
  const kycSub = verification.verified
    ? 'PAN + Aadhaar match'
    : kyc.complete
      ? 'PAN + ID on file'
      : kyc.missing?.join(' · ') || 'Upload docs below';

  return (
    <div className="mt-2 px-2 pb-8 md:px-4">
      {/* Same KPI card rhythm as Overview */}
      <div
        className={`mb-3 grid grid-cols-1 gap-3 ${
          readOnly ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'
        }`}>
        <div className="flex min-h-[5.5rem] flex-col justify-center rounded-xl border border-teal-200 bg-teal-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-teal-800/80">
            Deal status
          </p>
          <p className="mt-1 text-lg font-semibold text-teal-950">Closed Won</p>
          <p className="mt-1 truncate text-sm text-teal-900/80">
            {deal?.unit?.unitName || 'Unit assigned'}
          </p>
        </div>

        <div className="flex min-h-[5.5rem] flex-col justify-center rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Applicants
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {applicantCount ? `${applicantCount} on file` : 'None yet'}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {applicantCount ? 'Buyers for agreements' : 'Add primary buyer below'}
          </p>
        </div>

        <div
          className={`flex min-h-[5.5rem] flex-col justify-center rounded-xl border p-4 ${
            verification.verified
              ? 'border-emerald-200 bg-emerald-50'
              : kycComplete
                ? 'border-amber-200 bg-amber-50'
                : 'border-amber-200 bg-amber-50'
          }`}>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
            KYC documents
          </p>
          <p className="mt-1 text-lg font-semibold">{kycTitle}</p>
          <p className="mt-1 truncate text-sm opacity-90">{kycSub}</p>
        </div>

        {!readOnly && (
          <div className="flex min-h-[5.5rem] flex-col justify-center rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Agreements
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {agreementCount ? `${agreementCount} generated` : 'None yet'}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {agreementCount ? 'Draft / signed below' : 'Generate from templates'}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {!readOnly && (
          <DealApplicantsSection
            dealId={dealId}
            applicants={applicationData ?? []}
            onUpdated={refreshAll}
          />
        )}

        <DealKycDocumentsSection
          dealId={dealId}
          attachments={attachments}
          onUpdated={refreshAll}
          readOnly={readOnly}
        />

        {!readOnly && (
          <DealAgreementDocsSection
            dealId={dealId}
            projectId={projectId}
            generatedDocs={generatedDocs ?? []}
            onUpdated={refreshAll}
          />
        )}

        {!readOnly && (
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
              <h3 className="text-sm font-semibold text-gray-900">Other attachments</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Signed copies, bank letters, and extras for this deal.
              </p>
            </div>
            <div className="p-4 sm:p-5">
              <DealDocumentAttachments dealId={dealId} onDocumentsChange={refreshAll} embedded />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PostSalesDealHub;
